import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { CloseIcon } from "../../../assets/icons";
import {
  Overlay,
  Modal,
  CloseButton,
  Header,
} from "../../../components/UploadButton/uploadButtonModals/SelectTemplateStyled";
import {
  fetchSalesforceFields,
  fetchTransformationChoices,
  fetchPopularFields,
  fetchSignTemplateMappingRules,
} from "../../../service/settings";
import {
  buildSignTemplateRulesPayload,
  createDefaultSignTemplateRule,
  mapSignTemplateRulesFromApi,
  parseSignTemplateMappingApiErrors,
  SIGN_TEMPLATE_OPERATORS,
  validateSignTemplateRules,
} from "./signTemplateMappingHelpers";

const WideModal = styled(Modal)`
  width: min(96vw, 980px);
  max-height: 86vh;
  display: flex;
  flex-direction: column;
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
  margin-top: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
  &:focus {
    border-color: #0C53BB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  resize: vertical;
  min-height: 60px;
  outline: none;
  &:focus {
    border-color: #0C53BB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
`;

const TemplateList = styled.div`
  margin-top: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  max-height: 200px;
  overflow-y: auto;
`;

const TemplateOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #f9fafb;
  }
`;

const SelectedList = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SelectedItem = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: start;
  gap: 8px;
  padding: 6px 10px;
  background: #eff6ff;
  border-radius: 6px;
  font-size: 13px;
`;

const OutputNameInput = styled(Input)`
  margin-top: 6px;
  font-size: 12px;
  padding: 6px 8px;
  background: #fff;
`;

const OrderBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #0C53BB;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  margin-right: 8px;
`;

const MoveButtons = styled.div`
  display: flex;
  gap: 4px;
`;

const SmallBtn = styled.button`
  padding: 2px 6px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #fff;
  font-size: 11px;
  cursor: pointer;
  &:hover {
    background: #f3f4f6;
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const RemoveBtn = styled.button`
  padding: 2px 6px;
  border: 1px solid #fca5a5;
  border-radius: 4px;
  background: #fff;
  color: #dc2626;
  font-size: 11px;
  cursor: pointer;
  &:hover {
    background: #fef2f2;
  }
`;

const Footer = styled.div`
  padding: 12px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const Btn = styled.button`
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$primary ? "#0C53BB" : "#d1d5db")};
  background: ${(p) => (p.$primary ? "#0C53BB" : "#fff")};
  color: ${(p) => (p.$primary ? "#fff" : "#374151")};
  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SearchInput = styled(Input)`
  margin-bottom: 8px;
`;

const SectionTitle = styled.div`
  margin-top: 20px;
  padding-top: 14px;
  border-top: 1px solid #e5e7eb;
  font-size: 13px;
  font-weight: 700;
  color: #111827;
`;

const HelpText = styled.div`
  font-size: 11px;
  color: #6b7280;
  line-height: 1.5;
  margin-top: 6px;
`;

const ErrorText = styled.div`
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 12px;
  white-space: pre-wrap;
`;

const RuleHeader = styled.div`
  display: grid;
  grid-template-columns: 130px minmax(260px, 1.6fr) 130px minmax(150px, 1fr) minmax(220px, 1.3fr) 56px auto;
  gap: 8px;
  margin-top: 12px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
`;

const RuleRow = styled.div`
  display: grid;
  grid-template-columns: 130px minmax(260px, 1.6fr) 130px minmax(150px, 1fr) minmax(220px, 1.3fr) 56px auto;
  gap: 8px;
  align-items: center;
  margin-top: 8px;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 12px;
  background: #fff;
`;

const Checkbox = styled.input`
  justify-self: center;
`;

const SOURCE_TO_OBJECT = {
  ACCOUNT_VETERAN: "account",
  ACCOUNT_CLAIMANT: "account",
  ACCOUNT_CLIENT: "account",
  USER_ATTORNEY: "user",
  MATTER: "matter",
  INTAKE: "intake",
};

export default function PackageModal({
  open,
  onClose,
  onSave,
  editingPackage,
  allTemplates,
  saving,
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [defaultSignTemplateName, setDefaultSignTemplateName] = useState("");
  const [signTemplateRules, setSignTemplateRules] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [sfFieldsCache, setSfFieldsCache] = useState({});
  const [popularFieldsByObject, setPopularFieldsByObject] = useState({});
  const [signMappingError, setSignMappingError] = useState("");
  const [signMappingLoading, setSignMappingLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (editingPackage) {
        setName(editingPackage.name || "");
        setDescription(editingPackage.description || "");
        setSelected(
          (editingPackage.items || []).map((it) => ({
            formstack_id: it.formstack_id,
            name: it.template_name,
            output_name: it.output_name_override || it.template_output_name || "",
          }))
        );
        setDefaultSignTemplateName(editingPackage.formstack_sign_template_name || "");
      } else {
        setName("");
        setDescription("");
        setSelected([]);
        setDefaultSignTemplateName("");
        setSignTemplateRules([]);
      }
      setSearch("");
      setSignMappingError("");
    }
  }, [open, editingPackage]);

  const loadSfFields = useCallback(async (objectType) => {
    if (!objectType) return;
    try {
      const res = await fetchSalesforceFields(objectType);
      const data = res?.data || res;
      setSfFieldsCache((prev) =>
        prev[objectType] ? prev : { ...prev, [objectType]: data.fields || [] }
      );
    } catch (err) {
      console.error(`Failed to load Salesforce fields for ${objectType}`, err);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    async function loadSignMappingOptions() {
      setSignMappingLoading(true);
      try {
        const [choicesRes, popularRes, mappingRes] = await Promise.all([
          fetchTransformationChoices(),
          fetchPopularFields().catch(() => null),
          editingPackage?.id
            ? fetchSignTemplateMappingRules(editingPackage.id).catch((err) => ({ __error: err }))
            : Promise.resolve(null),
        ]);
        if (cancelled) return;

        const choicesData = choicesRes?.data || choicesRes;
        setDataSources(choicesData.data_sources || []);

        if (popularRes) {
          const popData = popularRes?.data || popularRes;
          const byObj = popData.popular_fields || {};
          const namesByObj = {};
          for (const [obj, items] of Object.entries(byObj)) {
            namesByObj[obj] = items.map((i) => i.name);
          }
          setPopularFieldsByObject(namesByObj);
        }

        if (mappingRes?.__error) {
          setSignMappingError(parseSignTemplateMappingApiErrors(mappingRes.__error).join("\n"));
          return;
        }

        if (mappingRes) {
          const mappingData = mappingRes?.data || mappingRes;
          setDefaultSignTemplateName(mappingData.default_formstack_sign_template_name || "");
          const mappedRules = mapSignTemplateRulesFromApi(mappingData.rules || []);
          setSignTemplateRules(mappedRules);
          mappedRules.forEach((rule) => {
            const objType = SOURCE_TO_OBJECT[rule.sf_object];
            if (objType) loadSfFields(objType);
          });
        }
      } catch (err) {
        if (!cancelled) setSignMappingError("Failed to load Formstack Sign overlay settings.");
      } finally {
        if (!cancelled) setSignMappingLoading(false);
      }
    }

    loadSignMappingOptions();
    return () => {
      cancelled = true;
    };
  }, [open, editingPackage?.id, loadSfFields]);

  const groupedDataSources = dataSources.reduce((groups, ds) => {
    const groupName = ds.group || "";
    let group = groups.find((item) => item.group === groupName);
    if (!group) {
      group = { group: groupName, items: [] };
      groups.push(group);
    }
    group.items.push(ds);
    return groups;
  }, []);

  const toggleTemplate = (tmpl) => {
    setSelected((prev) => {
      const exists = prev.find((s) => s.formstack_id === tmpl.formstack_id);
      if (exists) return prev.filter((s) => s.formstack_id !== tmpl.formstack_id);
      return [
        ...prev,
        {
          formstack_id: tmpl.formstack_id,
          name: tmpl.name,
          output_name: tmpl.output_name || "",
        },
      ];
    });
  };

  const updateOutputName = (formstackId, outputName) => {
    setSelected((prev) =>
      prev.map((item) =>
        item.formstack_id === formstackId
          ? { ...item, output_name: outputName }
          : item
      )
    );
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    setSelected((prev) => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  };

  const moveDown = (idx) => {
    setSelected((prev) => {
      if (idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  };

  const removeItem = (formstackId) => {
    setSelected((prev) => prev.filter((s) => s.formstack_id !== formstackId));
  };

  const handleSubmit = () => {
    const validationErrors = validateSignTemplateRules(signTemplateRules);
    if (validationErrors.length > 0) {
      setSignMappingError(validationErrors.join("\n"));
      return;
    }

    const signTemplateMapping = buildSignTemplateRulesPayload(
      signTemplateRules,
      defaultSignTemplateName
    );

    onSave({
      name: name.trim(),
      description: description.trim(),
      formstack_sign_template_name: defaultSignTemplateName.trim(),
      templates: selected.map((s, i) => ({
        formstack_id: s.formstack_id,
        order_index: i,
        output_name: (s.output_name || "").trim(),
      })),
      sign_template_mapping: signTemplateMapping,
    });
  };

  const filteredTemplates = allTemplates.filter((t) => {
    if (!search) return true;
    return t.name.toLowerCase().includes(search.toLowerCase());
  });

  if (!open) return null;

  return (
    <Overlay>
      <WideModal>
        <CloseButton onClick={onClose} aria-label="Close package modal">
          <CloseIcon />
        </CloseButton>

        <Header>{editingPackage ? "Edit Package" : "Create Package"}</Header>

        <Body>
          <Label>Package Name</Label>
          <Input
            placeholder="e.g. New Client Signature Package"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <Label>Description</Label>
          <TextArea
            placeholder="Optional description..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <Label>Selected Templates ({selected.length})</Label>
          {selected.length > 0 && (
            <SelectedList>
              {selected.map((s, idx) => (
                <SelectedItem key={s.formstack_id}>
                  <div>
                    <OrderBadge>{idx + 1}</OrderBadge>
                    {s.name}
                    <OutputNameInput
                      placeholder="Generated name, e.g. 1 VA contract"
                      value={s.output_name || ""}
                      onChange={(e) => updateOutputName(s.formstack_id, e.target.value)}
                    />
                  </div>
                  <MoveButtons>
                    <SmallBtn onClick={() => moveUp(idx)} disabled={idx === 0}>
                      &#8593;
                    </SmallBtn>
                    <SmallBtn
                      onClick={() => moveDown(idx)}
                      disabled={idx === selected.length - 1}
                    >
                      &#8595;
                    </SmallBtn>
                    <RemoveBtn onClick={() => removeItem(s.formstack_id)}>
                      &#10005;
                    </RemoveBtn>
                  </MoveButtons>
                </SelectedItem>
              ))}
            </SelectedList>
          )}

          <SectionTitle>Formstack Sign Overlay</SectionTitle>
          <HelpText>
            Set the Formstack Sign template for documents generated from this package.
            Conditional rules are checked top to bottom; the first match wins.
          </HelpText>
          {signMappingError && <ErrorText role="alert">{signMappingError}</ErrorText>}
          <Label>Default Formstack Sign Template</Label>
          <Input
            placeholder="e.g. Test DMS - automated template"
            value={defaultSignTemplateName}
            onChange={(e) => {
              setDefaultSignTemplateName(e.target.value);
              setSignMappingError("");
            }}
          />
          <HelpText>
            Used when no conditional rule matches. This must exactly match the template name in Formstack Sign.
          </HelpText>

          {signTemplateRules.length > 0 && (
            <>
              <RuleHeader>
                <span>Data Source</span>
                <span>Salesforce Field</span>
                <span>Operator</span>
                <span>Expected Value</span>
                <span>Sign Template</span>
                <span>Active</span>
                <span />
              </RuleHeader>
              {signTemplateRules.map((entry, idx) => {
                const objType = SOURCE_TO_OBJECT[entry.sf_object];
                const sfFields = sfFieldsCache[objType] || [];
                return (
                  <RuleRow key={entry._key}>
                    <Select
                      value={entry.sf_object || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSignTemplateRules((prev) =>
                          prev.map((rule, i) =>
                            i === idx ? { ...rule, sf_object: val, sf_field_name: "" } : rule
                          )
                        );
                        setSignMappingError("");
                        const newObjType = SOURCE_TO_OBJECT[val];
                        if (newObjType) loadSfFields(newObjType);
                      }}
                    >
                      <option value="">--</option>
                      {groupedDataSources.map((g) =>
                        g.group ? (
                          <optgroup key={g.group} label={g.group}>
                            {g.items.map((ds) => (
                              <option key={ds.value} value={ds.value}>
                                {ds.label}
                              </option>
                            ))}
                          </optgroup>
                        ) : (
                          g.items.map((ds) => (
                            <option key={ds.value} value={ds.value}>
                              {ds.label}
                            </option>
                          ))
                        )
                      )}
                    </Select>
                    <Select
                      value={entry.sf_field_name || ""}
                      disabled={!entry.sf_object}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSignTemplateRules((prev) =>
                          prev.map((rule, i) =>
                            i === idx ? { ...rule, sf_field_name: val } : rule
                          )
                        );
                        setSignMappingError("");
                      }}
                    >
                      <option value="">--</option>
                      {sfFields.map((field) => (
                        <option key={field.name} value={field.name}>
                          {field.label || field.name}
                          {popularFieldsByObject[objType]?.includes(field.name) ? " *" : ""}
                        </option>
                      ))}
                    </Select>
                    <Select
                      value={entry.operator || "equals"}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSignTemplateRules((prev) =>
                          prev.map((rule, i) =>
                            i === idx ? { ...rule, operator: val } : rule
                          )
                        );
                        setSignMappingError("");
                      }}
                    >
                      {SIGN_TEMPLATE_OPERATORS.map((op) => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </Select>
                    <Input
                      value={entry.expected_value}
                      placeholder="External Attorney Referral"
                      onChange={(e) => {
                        const val = e.target.value;
                        setSignTemplateRules((prev) =>
                          prev.map((rule, i) =>
                            i === idx ? { ...rule, expected_value: val } : rule
                          )
                        );
                        setSignMappingError("");
                      }}
                    />
                    <Input
                      value={entry.formstack_sign_template_name}
                      placeholder="Referral overlay template"
                      onChange={(e) => {
                        const val = e.target.value;
                        setSignTemplateRules((prev) =>
                          prev.map((rule, i) =>
                            i === idx
                              ? { ...rule, formstack_sign_template_name: val }
                              : rule
                          )
                        );
                        setSignMappingError("");
                      }}
                    />
                    <Checkbox
                      type="checkbox"
                      checked={entry.is_active !== false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSignTemplateRules((prev) =>
                          prev.map((rule, i) =>
                            i === idx ? { ...rule, is_active: checked } : rule
                          )
                        );
                      }}
                    />
                    <RemoveBtn
                      type="button"
                      onClick={() => {
                        setSignTemplateRules((prev) => prev.filter((_, i) => i !== idx));
                        setSignMappingError("");
                      }}
                    >
                      remove
                    </RemoveBtn>
                  </RuleRow>
                );
              })}
            </>
          )}
          <SmallBtn
            type="button"
            style={{ marginTop: 10 }}
            disabled={signMappingLoading}
            onClick={() => {
              setSignTemplateRules((prev) => [...prev, createDefaultSignTemplateRule()]);
              setSignMappingError("");
            }}
          >
            + Add Conditional Rule
          </SmallBtn>

          <Label>Add Templates</Label>
          <SearchInput
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <TemplateList>
            {filteredTemplates.map((t) => {
              const isChecked = selected.some(
                (s) => s.formstack_id === t.formstack_id
              );
              return (
                <TemplateOption key={t.formstack_id}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleTemplate(t)}
                  />
                  <span>{t.name}</span>
                  <span style={{ color: "#9ca3af", fontSize: 11, marginLeft: "auto" }}>
                    {t.file_type?.toUpperCase()}
                  </span>
                </TemplateOption>
              );
            })}
            {filteredTemplates.length === 0 && (
              <div style={{ padding: 12, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                No templates found
              </div>
            )}
          </TemplateList>
        </Body>

        <Footer>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn
            $primary
            disabled={!name.trim() || selected.length === 0 || saving}
            onClick={handleSubmit}
          >
            {saving ? "Saving..." : editingPackage ? "Update Package" : "Create Package"}
          </Btn>
        </Footer>
      </WideModal>
    </Overlay>
  );
}
